import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { conditionsText, EVENT_KIND_LABELS, INVOICE_SETTINGS_RESOURCE, INVOICES_KEY, INVOICES_RESOURCE, type LineRule, type LineRuleFields, type LineRulesView, type Tri } from './types';
import './invoices.css';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

const KEY = [...INVOICES_KEY, 'line-rules'];
const TRI_LABELS: Record<Tri, string> = { dowolnie: 'dowolnie', tak: 'tak', nie: 'nie' };
const EMPTY: LineRuleFields = { name: '', equipment: 'dowolnie', services: 'dowolnie', serviceIds: [], eventKind: 'dowolny', lineText: '', transport: 'osobno', transportText: 'Transport' };

const errorText = (e: unknown) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zapisać');

/** Only the fields that changed (rule 7a), all of them for a new rule. */
export function ruleChanges(draft: LineRuleFields, base: LineRule | null): Partial<LineRuleFields> {
  if (!base) return draft;
  const out: Partial<LineRuleFields> = {};
  for (const k of Object.keys(draft) as Array<keyof LineRuleFields>) if (JSON.stringify(draft[k]) !== JSON.stringify(base[k])) (out as Record<string, unknown>)[k] = draft[k];
  return out;
}

/** Adding or editing one rule: name, conditions, the line's text and the transport. */
function RuleDialog({ rule, services, onSaved, onClose, onReload }: { rule: LineRule | null; services: LineRulesView['services']; onSaved: (v: LineRulesView) => void; onClose: () => void; onReload: () => void }) {
  const [d, setD] = useState<LineRuleFields>(rule ? { name: rule.name, equipment: rule.equipment, services: rule.services, serviceIds: rule.serviceIds, eventKind: rule.eventKind, lineText: rule.lineText, transport: rule.transport, transportText: rule.transportText } : EMPTY);
  const save = useMutation({
    mutationFn: (body: Partial<LineRuleFields>) =>
      rule ? api<LineRulesView>(`/api/invoices/line-rules/${rule.id}`, { method: 'PATCH', body: { ...body, version: rule.version } }) : api<LineRulesView>('/api/invoices/line-rules', { method: 'POST', body }),
    onSuccess: onSaved,
  });
  const set = (patch: Partial<LineRuleFields>) => setD({ ...d, ...patch });
  const issue = (path: string) => (save.error instanceof ApiError ? fieldIssues(save.error).find((i) => i.path === path)?.message : undefined);
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = ruleChanges(d, rule);
    if (Object.keys(body).length === 0) onClose();
    else save.mutate(body);
  }
  // owner 2026-10-23 21:14: Escape with a changed rule asks first
  const guardRef = useUnsavedGuard({
    dirty: Object.keys(ruleChanges(d, rule)).length > 0 && (rule !== null || !sameValues(d, EMPTY)),
    save: async (el) => {
      if (!validForm(el)) return false;
      await save.mutateAsync(ruleChanges(d, rule));
      return true;
    },
  });
  // a service of the rule that is no longer in the price list stays listed (to be unticked)
  const choices = [...services, ...d.serviceIds.filter((id) => !services.some((s) => s.id === id)).map((id) => ({ id, name: 'usunięta usługa' }))];
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  const general = save.error && !conflict && !(save.error instanceof ApiError && fieldIssues(save.error).length > 0) ? errorText(save.error) : null;
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel inv-rule-dialog" role="dialog" aria-modal="true" aria-labelledby="inv-rule-title" onSubmit={submit}>
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="inv-rule-title">{rule ? 'Reguła pozycji faktury' : 'Nowa reguła pozycji faktury'}</h2>
        {conflict && <ConflictNotice onReload={onReload} />}
        <label className="field">
          <span>Nazwa reguły</span>
          <input value={d.name} maxLength={200} onChange={(e) => set({ name: e.target.value })} autoFocus />
          {issue('name') && <span className="field-error">{issue('name')}</span>}
        </label>
        <fieldset>
          <legend>Kiedy pasuje (wszystkie warunki naraz)</legend>
          <label className="field">
            <span>Sprzęt w kosztorysie</span>
            <select value={d.equipment} onChange={(e) => set({ equipment: e.target.value as Tri })}>
              {(Object.keys(TRI_LABELS) as Tri[]).map((t) => (
                <option key={t} value={t}>
                  {TRI_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Usługi w kosztorysie</span>
            <select value={d.services} onChange={(e) => set({ services: e.target.value as Tri })}>
              {(Object.keys(TRI_LABELS) as Tri[]).map((t) => (
                <option key={t} value={t}>
                  {TRI_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          {d.services !== 'dowolnie' && (
            <div role="group" aria-label="Które usługi">
              <p className="hint">Które usługi z cennika liczą się do warunku (żadna zaznaczona – dowolna usługa z „Obsługi”):</p>
              <div className="inv-rule-services">
                {choices.map((s) => (
                  <label key={s.id} className="check">
                    <input type="checkbox" checked={d.serviceIds.includes(s.id)} onChange={(e) => set({ serviceIds: e.target.checked ? [...d.serviceIds, s.id] : d.serviceIds.filter((x) => x !== s.id) })} /> {s.name}
                  </label>
                ))}
              </div>
              {issue('serviceIds') && <span className="field-error">{issue('serviceIds')}</span>}
            </div>
          )}
          <label className="field">
            <span>Rodzaj</span>
            <select value={d.eventKind} onChange={(e) => set({ eventKind: e.target.value as LineRule['eventKind'] })}>
              {(Object.keys(EVENT_KIND_LABELS) as Array<LineRule['eventKind']>).map((k) => (
                <option key={k} value={k}>
                  {EVENT_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <fieldset>
          <legend>Co będzie na fakturze</legend>
          <label className="field">
            <span>Treść pozycji</span>
            <input value={d.lineText} maxLength={512} onChange={(e) => set({ lineText: e.target.value })} placeholder="np. Realizacja nagłośnienia" />
            {issue('lineText') && <span className="field-error">{issue('lineText')}</span>}
          </label>
          <p className="hint">Pola: {'{wydarzenie}'} – nazwa wydarzenia, {'{dni}'} – dni wydarzenia (np. „Realizacja nagłośnienia – {'{wydarzenie}'}, {'{dni}'}”).</p>
          <label className="check">
            <input type="radio" name="inv-rule-transport" checked={d.transport === 'osobno'} onChange={() => set({ transport: 'osobno' })} /> Transport jako osobna pozycja
          </label>
          <label className="check">
            <input type="radio" name="inv-rule-transport" checked={d.transport === 'w_pozycji'} onChange={() => set({ transport: 'w_pozycji' })} /> Transport w pozycji reguły
          </label>
          {d.transport === 'osobno' && (
            <label className="field">
              <span>Nazwa pozycji transportu</span>
              <input value={d.transportText} maxLength={512} onChange={(e) => set({ transportText: e.target.value })} />
              {issue('transportText') && <span className="field-error">{issue('transportText')}</span>}
            </label>
          )}
        </fieldset>
        {general && (
          <p className="alert" role="alert">
            {general}
          </p>
        )}
        <div className="actions">
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
          <button className="btn primary" type="submit" disabled={save.isPending}>
            Zapisz regułę
          </button>
        </div>
      </form>
    </div>
  );
}

/** "Sprawdź na wydarzeniu": which rule matches an event's accepted quote and the lines it gives. */
function RulesPreview({ version }: { version: string }) {
  const [eventId, setEventId] = useState('');
  const events = useQuery({ queryKey: [...KEY, 'preview-events'], queryFn: () => api<{ events: Array<{ id: string; name: string; startsAt: string; versionNumber: number }> }>('/api/invoices/line-rules/preview-events') });
  const preview = useQuery({
    queryKey: [...KEY, 'preview', eventId, version],
    enabled: eventId !== '',
    queryFn: () => api<{ rule: { id: string; name: string } | null; lines: Array<{ name: string; netGrosze?: number }> }>(`/api/invoices/line-rules/preview?eventId=${eventId}`),
  });
  const list = events.data?.events ?? [];
  const p = preview.data;
  return (
    <section className="inv-rules-preview" aria-label="Sprawdź na wydarzeniu">
      <h3>Sprawdź na wydarzeniu</h3>
      <label className="field">
        <span>Wydarzenie z zaakceptowanym kosztorysem</span>
        <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
          <option value="">{list.length === 0 ? '— brak wydarzeń z zaakceptowanym kosztorysem —' : '— wybierz —'}</option>
          {list.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name} · {new Date(e.startsAt).toLocaleDateString('pl-PL')} · v{e.versionNumber}
            </option>
          ))}
        </select>
      </label>
      {preview.error && (
        <p className="alert" role="alert">
          {errorText(preview.error)}
        </p>
      )}
      {p && (
        <div role="status" aria-label="Wynik sprawdzenia">
          <p className="inv-rule-note">{p.rule ? <>Pasuje reguła: <strong>{p.rule.name}</strong></> : 'Żadna reguła nie pasuje – faktura będzie według sekcji.'}</p>
          <ul>
            {p.lines.map((l, i) => (
              <li key={`${i}-${l.name}`}>
                <span>{l.name}</span>
                <span>{l.netGrosze === undefined ? '—' : `${formatPln(l.netGrosze)} netto`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * "Finanse → Ustawienia → Reguły pozycji faktury" (owner 2026-10-20 23:50): ordered rules — the first matching one
 * gives the draft's line ("Według reguł"); added, edited, moved up and down, deleted here, tried on an event below.
 */
export function LineRulesSettings({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const root = useRef<HTMLElement>(null);
  const rules = useQuery({ queryKey: KEY, queryFn: () => api<LineRulesView>('/api/invoices/line-rules') });
  const [editing, setEditing] = useState<LineRule | 'new' | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const editable = can(me, INVOICE_SETTINGS_RESOURCE, 'edit');
  const saved = (v: LineRulesView) => {
    setEditing(null);
    setDeleting(null);
    queryClient.setQueryData(KEY, v);
  };
  const order = useMutation({ mutationFn: (ids: string[]) => api<LineRulesView>('/api/invoices/line-rules/order', { method: 'POST', body: { ids } }), onSuccess: saved });
  const remove = useMutation({ mutationFn: (r: LineRule) => api<LineRulesView>(`/api/invoices/line-rules/${r.id}?version=${r.version}`, { method: 'DELETE' }), onSuccess: saved });
  const reload = () => {
    order.reset();
    remove.reset();
    setEditing(null);
    void rules.refetch();
  };
  const data = rules.data;
  // "⚙ Zarządzaj regułami" on the invoice card links here
  useEffect(() => {
    if (data && location.hash === '#reguly-pozycji') root.current?.scrollIntoView({ block: 'start' });
  }, [data, location.hash]);
  if (!data) return null;
  const names = new Map(data.services.map((s) => [s.id, s.name]));
  const move = (i: number, by: number) => {
    const ids = data.rules.map((r) => r.id);
    const [id] = ids.splice(i, 1);
    ids.splice(i + by, 0, id!);
    order.mutate(ids);
  };
  const error = order.error ?? remove.error;
  const conflict = error instanceof ApiError && error.status === 409;
  return (
    <section ref={root} id="reguly-pozycji" className="inv-rules" aria-label="Reguły pozycji faktury" data-feature-id="invoices.line_rules">
      <div className="inv-rules-head">
        <h2>Reguły pozycji faktury</h2>
        {editable && (
          <button className="btn small" type="button" onClick={() => setEditing('new')}>
            + Reguła
          </button>
        )}
      </div>
      <p className="hint">Nowa faktura „Według reguł” ma jedną pozycję o treści pierwszej reguły od góry, która pasuje do kosztorysu (i osobno transport). Gdy żadna nie pasuje – pozycje według sekcji.</p>
      {conflict && <ConflictNotice onReload={reload} />}
      {error && !conflict && (
        <p className="alert" role="alert">
          {errorText(error)}
        </p>
      )}
      <div className="table-wrap">
        <table className="table" aria-label="Reguły pozycji faktury">
          <thead>
            <tr>
              {editable && <th className="inv-rule-order">Kolejność</th>}
              <th>Reguła i warunki</th>
              <th>Treść pozycji</th>
              <th className="wide-only">Transport</th>
              {editable && <th aria-label="Działania" />}
            </tr>
          </thead>
          <tbody>
            {data.rules.map((r, i) => (
              <tr key={r.id}>
                {editable && (
                  <td className="inv-rule-order">
                    <button className="btn icon" type="button" aria-label={`W górę: ${r.name}`} disabled={i === 0 || order.isPending} onClick={() => move(i, -1)}>
                      ↑
                    </button>
                    <button className="btn icon" type="button" aria-label={`W dół: ${r.name}`} disabled={i === data.rules.length - 1 || order.isPending} onClick={() => move(i, 1)}>
                      ↓
                    </button>
                  </td>
                )}
                <td>
                  <strong>
                    {i + 1}. {r.name}
                  </strong>
                  <span className="inv-rule-conditions">{conditionsText(r, names)}</span>
                </td>
                <td>{r.lineText}</td>
                <td className="wide-only">{r.transport === 'osobno' ? `osobno: ${r.transportText}` : 'w pozycji'}</td>
                {editable && (
                  <td className="num">
                    {deleting === r.id ? (
                      <span className="inv-rules-confirm">
                        <button className="btn small primary" type="button" aria-label={`Usuń regułę ${r.name}`} disabled={remove.isPending} onClick={() => remove.mutate(r)}>
                          Usuń
                        </button>
                        <button className="btn small" type="button" onClick={() => setDeleting(null)}>
                          Nie
                        </button>
                      </span>
                    ) : (
                      <RowMenu
                        label={`Działania reguły ${r.name}`}
                        actions={[
                          { label: 'Edytuj', onSelect: () => setEditing(r) },
                          { label: 'Usuń', danger: true, onSelect: () => setDeleting(r.id) },
                        ]}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
            {data.rules.length === 0 && (
              <tr>
                <td colSpan={editable ? 5 : 3} className="muted">
                  Brak reguł – faktury będą według sekcji.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {can(me, INVOICES_RESOURCE, 'view') && <RulesPreview version={data.rules.map((r) => `${r.id}:${r.version}:${r.sort}`).join(',')} />}
      {editing && <RuleDialog rule={editing === 'new' ? null : editing} services={data.services} onSaved={saved} onClose={() => setEditing(null)} onReload={reload} />}
    </section>
  );
}
